import { useCallback, useEffect, useState } from 'react';

// 3h while we validate this on staging. Raise to ~48h before enabling in
// production — we deploy most days, and a prompt per deploy is just noise.
export const NUDGE_INTERVAL_HOURS = 3;
export const NUDGE_INTERVAL_MS = NUDGE_INTERVAL_HOURS * 60 * 60 * 1000;
const NUDGE_STORAGE_KEY = 'kula:stale-build-nudged-at';

const readLastNudgedAt = (): number => {
  try {
    const at = Number(window.localStorage.getItem(NUDGE_STORAGE_KEY)) || 0;
    // A backwards clock correction would otherwise leave this permanently in
    // the future and stop nudging for good.
    return at > Date.now() ? 0 : at;
  } catch {
    return 0;
  }
};

const writeLastNudgedAt = (at: number): void => {
  try {
    window.localStorage.setItem(NUDGE_STORAGE_KEY, String(at));
  } catch {
    // Blocked storage just means the throttle does not survive a reload.
  }
};

/**
 * Whether to show the nudge for a stale tab: at most once per interval,
 * remembered across reloads, and hidden for good once dismissed. Detection
 * is the hook's job and is never throttled; only the asking is.
 */
export const useNudgeThrottle = (
  isStale: boolean,
  nudgeIntervalMs = NUDGE_INTERVAL_MS
) => {
  const [shown, setShown] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isStale || shown || dismissed) return;
    if (Date.now() - readLastNudgedAt() < nudgeIntervalMs) return;
    writeLastNudgedAt(Date.now());
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot: records the nudge in storage and shows it, once per stale episode
    setShown(true);
  }, [isStale, shown, dismissed, nudgeIntervalMs]);

  // The interval was recorded when the nudge appeared, so dismissing only has
  // to hide it — the next one is already due `nudgeIntervalMs` from now.
  const dismiss = useCallback(() => setDismissed(true), []);

  return { show: shown && !dismissed, dismiss };
};
